import { useMemo } from "react";
import type { Map } from "mapbox-gl";
import type { PlacePreviewFeature } from "~/lib/api";
import { MapMarker } from "~/components/map/primitives/MapMarker";
import { PlaceTypeIcon } from "~/components/ui/icons/place-type-icons";
import { isNearExistingPlace } from "./utils";

interface SearchResultMarkersProps {
  map: Map | null;
  features: PlacePreviewFeature[];
  /** Don't render marker at these coords (e.g., selected place) */
  excludeCoordinates?: [number, number] | null;
  onSelect: (feature: PlacePreviewFeature) => void;
}

/** Renders map markers for search results (Mapbox POIs and matched local places) */
export function SearchResultMarkers({ map, features, excludeCoordinates, onSelect }: SearchResultMarkersProps) {
  const filteredFeatures = useMemo(() => {
    if (!excludeCoordinates) return features;
    return features.filter(
      (f) => !isNearExistingPlace(f.geometry.coordinates as [number, number], [excludeCoordinates]),
    );
  }, [features, excludeCoordinates]);

  return (
    <>
      {filteredFeatures.map((feature) => (
        <MapMarker
          key={feature.properties.id || `search-${feature.geometry.coordinates.join(",")}`}
          map={map}
          coordinates={feature.geometry.coordinates as [number, number]}
          onClick={() => onSelect(feature)}
          label={feature.properties.name}
        >
          <PlaceTypeIcon type={feature.properties.type} className="size-4 text-primary-foreground" />
        </MapMarker>
      ))}
    </>
  );
}
